import React from 'react';

export interface MicroSoftRoundIconProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 40). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
}

/**
 * Microsoft four-square mark in a bordered white circle, tinted with the Bombiix
 * palette rather than Microsoft's brand colours. Used for in-product decoration
 * (the empty-state directory tile) — never for a Microsoft-branded sign-in button,
 * which must carry the official logo.
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the round Microsoft mark SVG element.
 */
function MicroSoftRoundIcon({ size = 40, className = '', title, ...rest }: MicroSoftRoundIconProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path
        d="M20 0.90918C30.5436 0.90918 39.0908 9.45638 39.0908 20C39.0908 30.5436 30.5436 39.0908 20 39.0908C9.45638 39.0908 0.90918 30.5436 0.90918 20C0.90918 9.45638 9.45638 0.90918 20 0.90918Z"
        fill="var(--color-white)"
        stroke="#E2E8F0"
        strokeWidth="1.81818"
      />
      <path
        d="M18.182 10.9082H13.6365C12.6324 10.9082 11.8184 11.7222 11.8184 12.7264V17.2718C11.8184 18.276 12.6324 19.09 13.6365 19.09H18.182C19.1861 19.09 20.0002 18.276 20.0002 17.2718V12.7264C20.0002 11.7222 19.1862 10.9082 18.182 10.9082Z"
        fill="var(--color-red)"
      />
      <path
        d="M18.182 20H13.6365C12.6324 20 11.8184 20.814 11.8184 21.8182V26.3636C11.8184 27.3678 12.6324 28.1818 13.6365 28.1818H18.182C19.1861 28.1818 20.0002 27.3678 20.0002 26.3636V21.8182C20.0002 20.814 19.1862 20 18.182 20Z"
        fill="var(--color-blue)"
      />
      <path
        d="M27.2728 10.9082H22.7274C21.7232 10.9082 20.9092 11.7222 20.9092 12.7264V17.2718C20.9092 18.276 21.7232 19.09 22.7274 19.09H27.2728C28.277 19.09 29.091 18.276 29.091 17.2718V12.7264C29.091 11.7222 28.277 10.9082 27.2728 10.9082Z"
        fill="var(--color-green)"
      />
      <path
        d="M27.2728 20H22.7274C21.7232 20 20.9092 20.814 20.9092 21.8182V26.3636C20.9092 27.3678 21.7232 28.1818 22.7274 28.1818H27.2728C28.277 28.1818 29.091 27.3678 29.091 26.3636V21.8182C29.091 20.814 28.277 20 27.2728 20Z"
        fill="var(--color-amber)"
      />
    </svg>
  );
}

/** Pure presentational icon — memoised so it never re-renders unless its props change. */
export default React.memo(MicroSoftRoundIcon);
